import {
  createStyles,
  withStyles,
  Checkbox,
  ConfirmationCheckbox,
  FormControlLabel,
  Grid,
  KeypadInputField,
  useUser,
  withClientOptions,
  withDialogStackContext
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles, WithClientOptions, WithDialogStackContext } from '@paintscout/ui'
import {
  formatCurrency,
  getCompanyOptions,
  getDetails,
  getFeatures,
  getObjectLabels,
  setDetails
} from '@paintscout/util/builder'
import { parseNumber } from '@paintscout/util/calculator'
import { isQuoteEditable } from '@paintscout/util/util'
import type { WithQuoteContext } from '@paintscout/react-quote'
import { EditableToggle, withQuoteContext } from '@paintscout/react-quote'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import type { QuoteDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import BaseSidebarPage from '../../BaseSidebarPage'
import { DetailsItem, DiscountItem, SubtotalItem, TaxItem } from './DetailsItem'
import { DetailCard } from './DetailCard'

const styles = (theme: Theme) =>
  createStyles({
    select: {},
    alignRightInput: {
      textAlign: 'right'
    },
    showTotal: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2)
    },
    spacer: {
      marginTop: theme.spacing(1),
      background: 'none',
      border: 'none'
    },
    hr: {
      background: 'none',
      borderBottom: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      margin: theme.spacing(2, 1, 2, 0)
    },
    greyDetailsItem: {
      padding: theme.spacing(1, 1, 1, 0),
      background: theme.paintscout.palette.brand.Neutral.P94
    },
    subDetailsItem: {
      marginLeft: '3px',
      paddingLeft: theme.spacing(3),
      borderLeft: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    gap: {
      marginTop: theme.spacing(2),
      gap: theme.spacing(0.25),
      // all child grid items
      ' & > *': {
        width: 'calc(50% - 2px)',
        marginBottom: '2px'
      }
    }
  })

export interface QuoteDetailsFormProps
  extends WithStyles<typeof styles>,
    WithQuoteContext,
    WithClientOptions,
    WithDialogStackContext {
  editable: boolean
  onToggleEdit?: (editStatus: boolean) => void
  permissionToEdit?: boolean
}

const HOURLY_RATE_KEY = 'hourly-rate'

type ExpandedKey =
  | 'hourly-rate'
  | 'sub-total'
  | 'discount'
  | 'tax'
  | 'service-fee'
  | 'added-options'
  | 'additional-work'
  | 'total'

const debouncedUpdateQuote = debounce((quote, updateQuote: (args: { quote: QuoteDocument }) => void) => {
  updateQuote({ quote })
}, 300)

/**
 * Displays fields for the Quote Details
 */
function QuoteDetailsForm({
  quoteContext,
  clientOptions,
  permissionToEdit,
  onToggleEdit,
  classes,
  editable
}: QuoteDetailsFormProps) {
  const { quote } = quoteContext
  const { options } = clientOptions
  const [details, setStateDetails] = useState(getDetails({ quote, options }))
  const [expandedKey, setExpandedKey] = useState<ExpandedKey>(null)
  const objectLabels = getObjectLabels({ options })
  const features = getFeatures({ options })
  const materialCostFeature = get(features, 'quotes.materialCost')
  const salesRateFeature = get(features, 'quotes.salesRate')
  const additionalWorkFeature = get(features, 'quotes.additionalWork')
  const companyOptions = getCompanyOptions({ options })

  const additionalWorkLabour = details.totals.additionalWork
  const additionalMaterials = details.totals.additionalMaterials
  const totalAdditional = additionalWorkLabour + additionalMaterials
  const addedOptionsLabour = details.totals.addedOptions
  const addedOptionsMaterials = details.totals.addedOptionsMaterials
  const totalAddedOptions = addedOptionsLabour + addedOptionsMaterials

  const totalLabor = (details.totals.labour ? details.totals.labour : 0) + addedOptionsLabour + additionalWorkLabour
  const totalMaterials =
    (details.totals.materials ? details.totals.materials : 0) + addedOptionsMaterials + additionalMaterials

  const totalHours = details.totals.hours.useCustom ? details.totals.hours.custom : details.totals.hours.default

  const { trialExpired } = useUser()

  const [editToggleAnchor, setEditToggleAnchor] = useState<{
    top: number
    left: number
  }>(null)
  const isSmartTemplate = quote.type === 'quoteTemplate' && quote.smartTemplate
  const isEditable = permissionToEdit && (isQuoteEditable(quote) || editable) && !trialExpired

  useEffect(() => {
    setStateDetails(getDetails({ quote, options }))
  }, [quote])

  const updateQuote = (args: { quote: QuoteDocument }) => {
    debouncedUpdateQuote(args.quote, quoteContext.updateQuote)
  }

  const handleExpand = (key: ExpandedKey, expanded: boolean) => {
    setExpandedKey(expanded ? key : null)
  }

  return (
    <EditableToggle
      position={editToggleAnchor}
      editable={isEditable && !isSmartTemplate}
      disabled={trialExpired || !permissionToEdit}
      onToggle={() => {
        onToggleEdit(true)
      }}
      onClose={() => setEditToggleAnchor(null)}
    >
      <BaseSidebarPage>
        <div
          onClick={(ev: React.MouseEvent) => {
            function inExpansionPanelSummary(element) {
              if (element.classList && element.classList.contains('expansion-panel-summary')) {
                return true
              }
              return element.parentNode && inExpansionPanelSummary(element.parentNode)
            }

            if ((!isEditable || isSmartTemplate) && !inExpansionPanelSummary(ev.target)) {
              ev.stopPropagation()
              setEditToggleAnchor({
                top: ev.clientY,
                left: ev.clientX
              })
            }
          }}
        >
          <DetailsItem
            label={`Hourly Rate (${objectLabels.currency.symbol})`}
            value={details.hourlyRate}
            expanded={expandedKey === HOURLY_RATE_KEY}
            onExpand={() => handleExpand(HOURLY_RATE_KEY, expandedKey !== HOURLY_RATE_KEY)}
            detailsComponent={
              <KeypadInputField
                value={details.hourlyRate}
                classes={{ inputInput: classes.alignRightInput }}
                margin="dense"
                name={HOURLY_RATE_KEY}
                onChange={handleHourlyRateChange}
                fullWidth={true}
                autoSelect={true}
                format={'price'}
                disabled={!companyOptions.hourlyRate.allowCustom || !isEditable || isSmartTemplate}
              />
            }
          />
          <hr className={classes.hr} />

          <SubtotalItem
            expanded={expandedKey === 'sub-total'}
            onExpand={(expanded: boolean) => handleExpand('sub-total', expanded)}
            disabled={!isEditable || isSmartTemplate}
            onChange={updateQuote}
          />

          {!!totalAddedOptions && (
            <DetailsItem
              label={'Options Added'}
              value={totalAddedOptions}
              expanded={expandedKey === 'added-options'}
              onExpand={(expanded: boolean) => handleExpand('added-options', expanded)}
              detailsComponent={
                <div className={classes.subDetailsItem}>
                  <DetailsItem label={'Labor'} value={addedOptionsLabour} />
                  <DetailsItem label={'Materials'} value={addedOptionsMaterials} />
                </div>
              }
            />
          )}

          {additionalWorkFeature && !!details.totals.additionalWork && (
            <DetailsItem
              label={'Addtnl Work'}
              value={totalAdditional}
              expanded={expandedKey === 'additional-work'}
              onExpand={(expanded: boolean) => handleExpand('additional-work', expanded)}
              detailsComponent={
                <div className={classes.subDetailsItem}>
                  <DetailsItem label={'Labor'} value={additionalWorkLabour} />
                  <DetailsItem label={'Materials'} value={additionalMaterials} />
                </div>
              }
            />
          )}

          <DiscountItem
            expanded={expandedKey === 'discount'}
            onExpand={(expanded: boolean) => handleExpand('discount', expanded)}
            disabled={!isEditable}
            onChange={updateQuote}
          />

          <TaxItem
            expanded={expandedKey === 'tax'}
            onExpand={(expanded: boolean) => handleExpand('tax', expanded)}
            disabled={!isEditable}
            onChange={updateQuote}
          />

          {details.tax.rate !== 0 || details.discount.rate !== 0}

          <hr className={classes.hr} />

          {/* <TotalItem label={'Total'} value={details.totals.afterTax} /> */}

          <DetailsItem
            label={'Total'}
            value={details.totals.afterTax}
            expanded={expandedKey === 'total'}
            variant="body1"
            onExpand={(expanded: boolean) => handleExpand('total', expanded)}
            detailsComponent={
              materialCostFeature ? (
                <div className={classes.subDetailsItem}>
                  <DetailsItem label={'Labor'} value={totalLabor} />
                  <DetailsItem label={'Materials'} value={totalMaterials} />
                </div>
              ) : null
            }
          />

          <Grid container justifyContent="space-between" alignItems="center" className={classes.gap}>
            <Grid item xs={!salesRateFeature ? 12 : undefined}>
              <DetailCard label={'Total Hours'} value={totalHours} />
            </Grid>
            {salesRateFeature && (
              <Grid item>
                <DetailCard
                  label={`Sales Rate`}
                  value={`${formatCurrency({ value: details.totals.salesRate, options })}/hr`}
                />
              </Grid>
            )}
          </Grid>

          <div className={classes.showTotal}>
            <FormControlLabel
              label={'Show Total'}
              control={
                <ConfirmationCheckbox
                  dialogTitle={'Are you sure?'}
                  dialogMessage={`This will remove the total from the ${objectLabels.quote.value}.`}
                  checked={details.totals.show}
                  direction={'dangerousToUncheck'}
                  disabled={!isEditable}
                  customOnChange={handleTotalsCheckboxChange('show')}
                />
              }
            />
            {quote.is_invoice && (
              <FormControlLabel
                label={'Show Balance Due'}
                control={
                  <Checkbox
                    checked={details.totals.showBalanceDue}
                    disabled={!isEditable}
                    onChange={handleTotalsCheckboxChange('showBalanceDue')}
                  />
                }
              />
            )}
          </div>
        </div>
      </BaseSidebarPage>
    </EditableToggle>
  )

  function handleTotalsCheckboxChange(name: string) {
    return function (ev: React.ChangeEvent<HTMLInputElement>, value: boolean) {
      const updatedDetails = {
        ...details,
        totals: {
          ...details.totals,
          [name]: value
        }
      }

      updateQuote({ quote: setDetails({ quote, options, details: updatedDetails }) })
    }
  }

  function handleHourlyRateChange(event: React.ChangeEvent<HTMLInputElement>) {
    const updatedDetails = {
      ...details,
      hourlyRate: parseNumber(event.target.value, 0)
    }
    setStateDetails(updatedDetails)
    updateQuote({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }
}

export default withStyles(styles)(withClientOptions(withQuoteContext(withDialogStackContext(QuoteDetailsForm))))
